/* eslint-disable react-hooks/exhaustive-deps */
import ScoreCardHeader from "../Views/ScoreCardHeader";
import "./ScoreCardMattersScreen.css";
import letsDoItIcon from "../../../assets/images/lets-do-it-icon.svg";
import curveImage from "../../../assets/images/score-card/curve-image.svg";
import emptyStar from "../../../assets/images/score-card/score-card-empty-star.svg";
import workStyleImage from "../../../assets/gif/why-this-matters.gif";
import energyWellnessImage from "../../../assets/gif/energy-matters.gif";
import leadershipImage from "../../../assets/gif/leadership.gif";
import scalingGraphImage from "../../../assets/gif/scaling-signals.gif";
import personalPatternsImage from "../../../assets/gif/personal-patterns.gif";
import { useLocation, useNavigate } from "react-router-dom";
import ROUTE_PATHS from "utils/routePaths";
import { useMutation, useQuery } from "@tanstack/react-query";
import useWSMApi from "services/api/useWSMApi";
import { useEffect } from "react";

export enum ScoreCardType {
  WORK_STYLE = "WORK_STYLE",
  ENERGY_WELLNESS = "ENERGY_WELLNESS",
  LEADERSHIP_TEAM = "LEADERSHIP_TEAM",
  SCALING_READINESS = "SCALING_READINESS",
  PERSONAL_PATTERNS = "PERSONAL_PATTERNS",
}

const SCORE_CARD_TYPES: ScoreCardType[] = [
  ScoreCardType.WORK_STYLE,
  ScoreCardType.ENERGY_WELLNESS,
  ScoreCardType.LEADERSHIP_TEAM,
  ScoreCardType.SCALING_READINESS,
  ScoreCardType.PERSONAL_PATTERNS,
];

const SCORE_CARD_IMAGE_MAP: Record<ScoreCardType, string> = {
  [ScoreCardType.WORK_STYLE]: workStyleImage,
  [ScoreCardType.ENERGY_WELLNESS]: energyWellnessImage,
  [ScoreCardType.LEADERSHIP_TEAM]: leadershipImage,
  [ScoreCardType.SCALING_READINESS]: scalingGraphImage,
  [ScoreCardType.PERSONAL_PATTERNS]: personalPatternsImage,
};

const ScoreCardMattersScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchScoreCard, addUserScoreCard } = useWSMApi();
  const currentTypeIndex = location.state?.currentTypeIndex ?? 0;
  const allAnswers = location.state?.allAnswers ?? {};
  const currentType = SCORE_CARD_TYPES[currentTypeIndex];
  const isAllTypesCompleted = currentTypeIndex >= SCORE_CARD_TYPES.length;

  const { data: scoreCardData, isLoading } = useQuery({
    queryKey: ["get-score-card", currentType],
    queryFn: () => fetchScoreCard({ type: currentType }),
    retry: false,
    enabled: !!currentType && !isAllTypesCompleted,
  });

  const addUserScoreCardMutation = useMutation({
    mutationFn: (data: any) => addUserScoreCard(data),
    onSuccess: () => {
      navigate(ROUTE_PATHS.WSM_SCRATCH_CARD_SCREEN, { replace: true });
    },
    onError: () => {
      navigate(ROUTE_PATHS.WSM_SCRATCH_CARD_SCREEN, { replace: true });
    },
  });

  useEffect(() => {
    if (isAllTypesCompleted && addUserScoreCardMutation.status === "idle") {
      const flattenedAnswers: Array<{ question: string; answer: string }> = [];
      Object.entries(allAnswers).forEach(([, section]) => {
        if (typeof section !== "object" || section === null) return;
        Object.entries(section).forEach(([questionId, answerData]) => {
          if (typeof answerData === "object" && answerData?.id) {
            flattenedAnswers.push({
              question: questionId,
              answer: answerData.id,
            });
          }
        });
      });
      addUserScoreCardMutation.mutate({ score_card_data: flattenedAnswers });
    }
  }, [isAllTypesCompleted]);

  const handleContinue = () => {
    if (!scoreCardData) return;
    navigate(ROUTE_PATHS.WSM_SCORE_CARD_QUESTION_SCREEN, {
      state: { scoreCardData, currentTypeIndex, allAnswers },
      replace: true,
    });
  };

  if (isAllTypesCompleted) {
    return (
      <div className="wms-mobile-container">
        <div className="score-card-matters-screen-container" />
      </div>
    );
  }

  if (isLoading || !scoreCardData) {
    return (
      <div className="wms-mobile-container">
        <div className="score-card-matters-screen-container" />
      </div>
    );
  }

  const cardImage = SCORE_CARD_IMAGE_MAP[currentType] ?? workStyleImage;

  const HEADER_ORANGE_FIRST_WORDS: Partial<Record<ScoreCardType, number>> = {
    [ScoreCardType.LEADERSHIP_TEAM]: 1,
    [ScoreCardType.SCALING_READINESS]: 1,
  };

  const displayTitle =
    scoreCardData?.header_title ?? scoreCardData?.score_card_title ?? "";
  const titleWords = displayTitle.trim().split(/\s+/).filter(Boolean);
  const orangeFirstCount = HEADER_ORANGE_FIRST_WORDS[currentType];

  let headerWhite: string;
  let headerOrange: string;

  if (orangeFirstCount) {
    headerOrange = titleWords.slice(0, orangeFirstCount).join(" ");
    headerWhite = titleWords.slice(orangeFirstCount).join(" ");
  } else {
    headerWhite = titleWords.slice(0, -1).join(" ");
    headerOrange = titleWords[titleWords.length - 1] ?? "";
  }

  return (
    <div className="wms-mobile-container">
      <div className="score-card-matters-screen-container">
        <div className="score-card-matters-screen-header-sticky">
          <ScoreCardHeader
            title={headerWhite}
            subTitle={headerOrange}
            orangeFirst={!!orangeFirstCount}
          />
        </div>
        <div className="score-card-matters-screen-content-container">
          <div className="score-card-matters-decoration" aria-hidden="true">
            <img src={curveImage} alt="" className="score-card-matters-curve" />
            <img src={emptyStar} alt="" className="score-card-matters-star" />
          </div>
          <div className="score-card-matters-card-glow">
            <div className="shadow-card">
              <div className="main-card">
                <img
                  src={cardImage}
                  alt="score-card-image"
                  className="card-image"
                />
                <span className="card-title">
                  {scoreCardData?.title?.includes(" ") ? (
                    <>
                      <span style={{ color: "#FF6B35" }}>
                        {scoreCardData.title.split(" ").slice(0, 2).join(" ")}
                      </span>{" "}
                      {scoreCardData.title.split(" ").slice(2).join(" ")}
                    </>
                  ) : (
                    scoreCardData?.title
                  )}
                </span>
                <div
                  dangerouslySetInnerHTML={{ __html: scoreCardData?.description }}
                  className="card-sub-title"
                >
                </div>
              </div>
            </div>
          </div>
          <div className="button-container" onClick={handleContinue}>
            <span>
              Continue to <span style={{ color: "#FF6B35" }}>Next</span>
            </span>
            <img src={letsDoItIcon} alt="right-arrow-icon" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScoreCardMattersScreen;
